// Every origin (localhost, dev tunnel, prod) must be listed as a redirect URI on osu!'s OAuth app.

import { AuthError, OsuUnavailableError, isUpstreamUnavailable, throwIfAuthOrUnavailable } from '../api/osuApi.js';

export interface OsuOAuthConfig {
  client_id: string;
}

interface StoredToken {
  accessToken:  string;
  refreshToken: string;
  expiresAtMs:  number;
  user?: { id: number; username: string; token: string };
}

interface OsuMe {
  id:       number;
  username: string;
}

const TOKEN_STORAGE_KEY = 'osu_auth_token';
// Login AND refresh must request the SAME scopes, or the
// token silently downgrades to `public` on its next renewal and /me breaks again.
const OAUTH_SCOPE = 'identify public';
// Browser redirects go to osu! directly; all fetch calls go through the proxy.
const AUTHORIZE_URL = 'https://osu.ppy.sh/oauth/authorize';
const PROXY_BASE    = 'https://proxy.replayviewer.com/osu-proxy';
const TOKEN_URL     = `${PROXY_BASE}/oauth/token`;
const API_BASE      = `${PROXY_BASE}/api/v2`;

function redirectUri(): string {
  return `${location.origin}/auth/osu/`;
}

let configPromise: Promise<OsuOAuthConfig> | null = null;

export function loadConfig(): Promise<OsuOAuthConfig> {
  if (configPromise === null) {
    configPromise = (async () => {
      const resp = await fetch('oauth-config.json', { cache: 'no-store' });
      if (!resp.ok) throw new Error(`oauth-config.json not found (HTTP ${resp.status})`);
      const cfg = await resp.json() as OsuOAuthConfig;
      if (!cfg.client_id) {
        throw new Error('oauth-config.json missing client_id');
      }
      return cfg;
    })().catch((err: unknown) => {
      configPromise = null;
      throw err;
    });
  }
  return configPromise;
}

export async function startLogin(): Promise<void> {
  const cfg = await loadConfig();
  // Stash deep-link fragment; the callback page redirects to '/' without the hash.
  if (location.hash) sessionStorage.setItem('deeplink_pending', location.hash.slice(1));
  const url = new URL(AUTHORIZE_URL);
  url.searchParams.set('client_id',     cfg.client_id);
  url.searchParams.set('redirect_uri',  redirectUri());
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope',         OAUTH_SCOPE);
  window.location.assign(url.toString());
}

export async function handlePendingCallback(): Promise<boolean> {
  const err = sessionStorage.getItem('osu_auth_error');
  if (err !== null) {
    sessionStorage.removeItem('osu_auth_error');
    throw new Error(`osu! login failed: ${err}`);
  }
  const code = sessionStorage.getItem('osu_auth_code');
  if (code === null) return false;
  sessionStorage.removeItem('osu_auth_code');

  const cfg = await loadConfig();
  const body = new URLSearchParams({
    client_id:    cfg.client_id,
    code,
    grant_type:   'authorization_code',
    redirect_uri: redirectUri(),
  });
  const resp = await fetch(TOKEN_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
    body,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`token exchange failed (HTTP ${resp.status}): ${text}`);
  }
  const data = await resp.json() as { access_token: string; refresh_token: string; expires_in: number };
  storeToken({
    accessToken:  data.access_token,
    refreshToken: data.refresh_token,
    expiresAtMs:  Date.now() + data.expires_in * 1000,
  });
  // Fires only on fresh code exchange (not refresh) so it counts completed sign-ins.
  void fetch('auth-ping', { method: 'POST' }).catch(() => {});
  return true;
}

function storeToken(tok: StoredToken): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tok));
}

function readToken(): StoredToken | null {
  const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (raw === null) return null;
  try { return JSON.parse(raw) as StoredToken; } catch { return null; }
}

export function isLoggedIn(): boolean {
  return readToken() !== null;
}

export function cachedUser(): OsuMe | null {
  const tok = readToken();
  if (tok?.user === undefined || tok.user.token !== tok.accessToken) return null;
  return { id: tok.user.id, username: tok.user.username };
}

export function logout(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

// Shared in-flight promise; without it, parallel callers race and the loser logs the user out.
let refreshInFlight: Promise<string> | null = null;

async function performRefresh(tok: StoredToken): Promise<string> {
  const cfg = await loadConfig();
  const body = new URLSearchParams({
    client_id:     cfg.client_id,
    grant_type:    'refresh_token',
    refresh_token: tok.refreshToken,
    scope:         OAUTH_SCOPE,
  });
  const resp = await fetch(TOKEN_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
    body,
  });
  if (!resp.ok) {
    // Wipe the stored token ONLY when osu! itself rejects the refresh token (JSON
    // 400/401, e.g. invalid_grant).
    const isJson = (resp.headers.get('content-type') || '').includes('application/json');
    if (isJson && (resp.status === 400 || resp.status === 401)) {
      logout();
      const text = await resp.text().catch(() => '');
      throw new AuthError(`token refresh rejected (HTTP ${resp.status}): ${text}`);
    }
    if (isUpstreamUnavailable(resp)) throw new OsuUnavailableError();
    const text = await resp.text().catch(() => '');
    throw new Error(`token refresh failed (HTTP ${resp.status}): ${text}`);
  }
  const data = await resp.json() as { access_token: string; refresh_token?: string; expires_in: number };
  const refreshed: StoredToken = {
    accessToken:  data.access_token,
    refreshToken: data.refresh_token ?? tok.refreshToken,
    expiresAtMs:  Date.now() + data.expires_in * 1000,
  };
  storeToken(refreshed);
  return refreshed.accessToken;
}

export async function getAccessToken(): Promise<string> {
  const tok = readToken();
  if (tok === null) throw new Error('not logged in');

  if (Date.now() < tok.expiresAtMs - 30_000) return tok.accessToken;

  if (refreshInFlight === null) {
    refreshInFlight = performRefresh(tok).finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

export async function fetchMe(): Promise<OsuMe> {
  // Propagates getAccessToken errors as-is: performRefresh throws AuthError only on a
  // genuine refresh rejection (token already wiped), OsuUnavailableError/Error otherwise.
  const token = await getAccessToken();

  // One retry on transient failures (5xx, cold start, edge propagation lag); a genuine
  // auth failure (JSON 401/403 via throwIfAuthOrUnavailable) fails fast. A Cloudflare
  // 403 HTML block classifies as OsuUnavailableError — NOT token expiry, so callers
  // don't log the (still-valid) user out.
  let lastErr: unknown;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const resp = await fetch(`${API_BASE}/me`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      if (!resp.ok) {
        throwIfAuthOrUnavailable(resp);
        throw new Error(`GET /me failed (HTTP ${resp.status})`);
      }
      const data = await resp.json() as { id: number; username: string };
      const me: OsuMe = { id: data.id, username: data.username };
      // Cache identity under the token it was fetched with (see StoredToken.user).
      const tok = readToken();
      if (tok !== null && tok.accessToken === token) {
        storeToken({ ...tok, user: { ...me, token } });
      }
      return me;
    } catch (err) {
      if (err instanceof AuthError) throw err;
      lastErr = err;
      if (attempt === 0) await new Promise((r) => setTimeout(r, 500));
    }
  }
  throw lastErr;
}
